import { useCallback, useEffect, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { PERMISSION_CATALOG, type PermissionPage, type TenantMembership } from '@orqforge/shared';
import { apiClient, type AccessGroup, type UpsertAccessGroup } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Input } from '../components/ui/Input';

type MemberProfile = TenantMembership & {
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
};

type GroupForm = UpsertAccessGroup & { id?: string; system?: boolean };

const ACTION_COLUMNS = [
  'read',
  'create',
  'update',
  'delete',
  'execute',
  'cancel',
  'decide',
  'message',
  'upload',
  'teach',
  'manage_members',
];

const selectClass =
  'h-9 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function usable(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || UUID_RE.test(trimmed)) return '';
  return trimmed;
}

function memberLabel(member: {
  userId: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  username?: string | null;
}): string {
  const display = usable(member.displayName);
  if (display) return display;
  const full = [usable(member.firstName), usable(member.lastName)].filter(Boolean).join(' ');
  if (full) return full;
  const email = usable(member.email);
  if (email) return email;
  const username = usable(member.username);
  if (username) return username;
  return 'Unnamed user';
}

function blank(operator: boolean, tenantId: string | null): GroupForm {
  return {
    name: '',
    description: '',
    permissions: {},
    allTenants: false,
    tenantIds: operator && tenantId ? [tenantId] : [],
  };
}

export function GroupsPage() {
  const { can, access, tenants, tenantId, user } = useApp();
  const operator = !!(access?.isPlatformUser || access?.allTenants || user.isOrqForgeAdmin);
  const catalog: PermissionPage[] = (access?.catalog?.length ? access.catalog : PERMISSION_CATALOG) as PermissionPage[];
  const visibleCatalog = operator ? catalog : catalog.filter((p) => p.scope === 'tenant' || p.scope === 'both');

  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<GroupForm>(() => blank(operator, tenantId));
  const [membersGroup, setMembersGroup] = useState<AccessGroup | null>(null);
  const [profiles, setProfiles] = useState<MemberProfile[]>([]);
  const [candidates, setCandidates] = useState<{ userId: string; label: string; email?: string }[]>([]);
  const [memberUserId, setMemberUserId] = useState('');
  const [pendingDelete, setPendingDelete] = useState<AccessGroup | null>(null);

  const applyGroup = useCallback((group: AccessGroup) => {
    setGroups((rows) => {
      const next = rows.map((row) => (row.id === group.id ? group : row));
      if (!next.some((row) => row.id === group.id)) next.push(group);
      return next;
    });
  }, []);

  const reload = useCallback(async () => {
    const rows = await apiClient.listGroups();
    setGroups(rows);
    setMembersGroup((open) => (open ? rows.find((g) => g.id === open.id) ?? open : open));
  }, []);

  const loadCandidates = useCallback(async (group: AccessGroup | null) => {
    if (!tenantId) {
      setCandidates([]);
      return;
    }
    try {
      const members = (await apiClient.listTenantMembers(tenantId)) as MemberProfile[];
      setProfiles(members);
      const taken = new Set((group?.members ?? []).map((m) => m.userId));
      setCandidates(
        members
          .filter((m) => !taken.has(m.userId))
          .map((m) => ({ userId: m.userId, label: memberLabel(m), email: m.email })),
      );
    } catch {
      setCandidates([]);
    }
  }, [tenantId]);

  useEffect(() => {
    void reload().catch((err) => setError(errText(err, 'Failed to load groups')));
  }, [reload]);

  function tenantName(id: string): string {
    return tenants.find((t) => t.id === id)?.name ?? id;
  }

  function label(member: { userId: string; email?: string | null }): string {
    const profile = profiles.find((p) => p.userId === member.userId);
    return memberLabel({
      userId: member.userId,
      email: profile?.email ?? member.email,
      displayName: profile?.displayName,
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      username: profile?.username,
    });
  }

  function togglePerm(page: string, action: string, on: boolean) {
    setForm((current) => {
      const permissions = { ...current.permissions, [page]: { ...(current.permissions[page] ?? {}) } };
      if (on) permissions[page][action] = true;
      else delete permissions[page][action];
      return { ...current, permissions };
    });
  }

  function toggleTenant(id: string, on: boolean) {
    setForm((current) => {
      const set = new Set(current.tenantIds ?? []);
      if (on) set.add(id);
      else set.delete(id);
      return { ...current, tenantIds: [...set] };
    });
  }

  async function save() {
    setError(null);
    try {
      const dto: UpsertAccessGroup = {
        name: form.name,
        description: form.description,
        permissions: form.permissions,
        allTenants: operator ? form.allTenants : false,
        tenantIds: operator && !form.allTenants ? form.tenantIds : [],
      };
      if (form.id) {
        const updated = await apiClient.updateGroup(form.id, dto);
        applyGroup(updated);
        setEditing(false);
        await reload();
        return;
      }
      const created = await apiClient.createGroup(dto);
      setForm({
        id: created.id,
        system: created.system,
        name: created.name,
        description: created.description,
        permissions: structuredClone(created.permissions),
        allTenants: created.allTenants,
        tenantIds: [...created.tenantIds],
      });
      setEditing(true);
      await reload();
      applyGroup(created);
    } catch (err) {
      setError(errText(err, 'Failed to save group'));
    }
  }

  async function addMember() {
    if (!membersGroup || !memberUserId) return;
    const candidate = candidates.find((c) => c.userId === memberUserId);
    const email = candidate?.email || (candidate?.label.includes('@') ? candidate.label : undefined);
    try {
      const updated = await apiClient.addGroupMember(membersGroup.id, { userId: memberUserId, email });
      applyGroup(updated);
      setMembersGroup(updated);
      setMemberUserId('');
      await reload();
      await loadCandidates(updated);
    } catch (err) {
      setError(errText(err, 'Failed to add member'));
    }
  }

  async function removeMember(userId: string) {
    if (!membersGroup) return;
    try {
      const updated = await apiClient.removeGroupMember(membersGroup.id, userId);
      applyGroup(updated);
      setMembersGroup(updated);
      await reload();
      await loadCandidates(updated);
    } catch (err) {
      setError(errText(err, 'Failed to remove member'));
    }
  }

  async function remove(group: AccessGroup) {
    try {
      await apiClient.deleteGroup(group.id);
      setPendingDelete(null);
      await reload();
    } catch (err) {
      setError(errText(err, 'Failed to delete group'));
      setPendingDelete(null);
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Groups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Named packs of page access. Packs also say which tenants they apply to. Union if a person is in several.
          </p>
        </div>
        {can('groups', 'create') ? (
          <Button
            size="sm"
            onClick={() => {
              setForm(blank(operator, tenantId));
              setMemberUserId('');
              setEditing(true);
            }}
          >
            New group
          </Button>
        ) : null}
      </div>

      {error ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</div>
      ) : null}

      {editing ? (
        <Card className="mb-6">
          <CardContent className="grid gap-4 pt-6">
            <h2 className="text-base font-semibold">{form.id ? 'Edit permissions' : 'New group'}</h2>
            <div className="grid max-w-xl gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-xs text-gray-500">Name</span>
                <Input value={form.name} disabled={!!form.system} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs text-gray-500">Description</span>
                <Input value={form.description ?? ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              {operator ? (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!form.allTenants}
                      onChange={(e) => setForm((f) => ({ ...f, allTenants: e.target.checked }))}
                    />
                    All tenants
                  </label>
                  {!form.allTenants ? (
                    <div>
                      <div className="mb-1 text-xs text-gray-500">Tenants</div>
                      {tenants.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 py-0.5 text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={(form.tenantIds ?? []).includes(t.id)}
                            onChange={(e) => toggleTenant(t.id, e.target.checked)}
                          />
                          {t.name}
                        </label>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-800">
                    <th className="py-2 pr-3">Page</th>
                    {ACTION_COLUMNS.map((action) => (
                      <th key={action} className="px-1 py-2 text-center">{action}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleCatalog.map((page) => (
                    <tr key={page.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{page.label}</div>
                        <div className="text-xs text-gray-500">{page.section}</div>
                      </td>
                      {ACTION_COLUMNS.map((action) => (
                        <td key={action} className="px-1 py-2 text-center">
                          {page.actions.includes(action) ? (
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={!!form.permissions[page.id]?.[action]}
                              onChange={(e) => togglePerm(page.id, action, e.target.checked)}
                            />
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={!form.name} onClick={() => void save()}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.length === 0 ? (
          <p className="text-sm text-gray-500">No groups yet.</p>
        ) : null}
        {groups.map((group) => (
          <Card key={group.id}>
            <CardContent className="grid gap-3 pt-6">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold">{group.name}</h2>
                  {group.description ? <p className="text-sm text-gray-500">{group.description}</p> : null}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {group.system ? <Badge variant="secondary">system</Badge> : null}
                    {operator && group.allTenants ? <Badge variant="secondary">All tenants</Badge> : null}
                    {operator && !group.allTenants
                      ? group.tenantIds.map((id) => <Badge key={id} variant="secondary">{tenantName(id)}</Badge>)
                      : null}
                  </div>
                </div>
                <DropdownMenu
                  trigger={<Button size="icon" variant="ghost" aria-label="Group actions"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    ...(can('groups', 'manage_members')
                      ? [{
                          label: 'Manage members',
                          onClick: () => {
                            setMembersGroup(group);
                            setMemberUserId('');
                            void loadCandidates(group);
                          },
                        }]
                      : []),
                    ...(can('groups', 'update')
                      ? [{
                          label: 'Edit permissions',
                          onClick: () => {
                            setForm({
                              id: group.id,
                              system: group.system,
                              name: group.name,
                              description: group.description,
                              permissions: structuredClone(group.permissions),
                              allTenants: group.allTenants,
                              tenantIds: [...group.tenantIds],
                            });
                            setMemberUserId('');
                            setEditing(true);
                          },
                        }]
                      : []),
                    ...(!group.system && can('groups', 'delete')
                      ? [{ label: 'Delete', className: 'text-red-600', onClick: () => setPendingDelete(group) }]
                      : []),
                  ]}
                />
              </div>
              <p className="text-sm text-gray-500">
                {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!membersGroup} onOpenChange={(open) => { if (!open) { setMembersGroup(null); setMemberUserId(''); } }}>
        <DialogContent maxWidth="md">
          <DialogHeader><DialogTitle>Manage members</DialogTitle></DialogHeader>
          <DialogBody>
            {membersGroup ? (
              <div className="grid gap-4">
                <p className="text-sm text-gray-500">{membersGroup.name}</p>
                <div className="flex flex-wrap gap-2">
                  {membersGroup.members.map((member) => (
                    <Badge key={member.userId} variant="outline">
                      {label(member)}
                      <button type="button" className="ml-1" aria-label="Remove member" onClick={() => void removeMember(member.userId)}>×</button>
                    </Badge>
                  ))}
                  {membersGroup.members.length === 0 ? <span className="text-sm text-gray-500">No members yet.</span> : null}
                </div>
                {can('groups', 'manage_members') ? (
                  <div className="flex max-w-xl gap-2">
                    <select className={selectClass} value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)}>
                      <option value="">Add a user...</option>
                      {candidates.map((candidate) => (
                        <option key={candidate.userId} value={candidate.userId}>{candidate.label}</option>
                      ))}
                    </select>
                    <Button size="sm" disabled={!memberUserId} onClick={() => void addMember()}>Add</Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button size="sm" onClick={() => { setMembersGroup(null); setMemberUserId(''); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader><DialogTitle>Delete group</DialogTitle></DialogHeader>
          <DialogBody>
            <p className="text-sm">Delete {pendingDelete?.name}? Members lose this group&apos;s access.</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => pendingDelete && void remove(pendingDelete)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
